import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/utils/api';
import {
  Package, Wallet, Check, CreditCard, Loader2, Clock,
  AlertTriangle, XCircle, RefreshCw, ExternalLink, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

export default function SubscriptionSettings() {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sessionId = searchParams.get('session_id') || searchParams.get('fulfillment_session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (sessionId && success === 'true') {
      verifyCheckout(sessionId);
    } else if (canceled === 'true') {
      toast.error('Checkout was canceled');
      setSearchParams({});
      loadData();
    } else {
      loadData();
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes] = await Promise.all([
        api.get('/subscriptions/status').catch(() => ({ data: null })),
        api.get('/subscriptions/plans').catch(() => ({ data: { plans: [] } })),
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans || []);
    } finally {
      setLoading(false);
    }
  };

  const verifyCheckout = async (sessionId) => {
    try {
      setLoading(true);
      const res = await api.post('/subscriptions/verify-checkout', { session_id: sessionId });
      const subType = res.data?.subscription_type;
      if (subType === 'fulfillment') {
        toast.success('Fulfillment subscription activated! Your store now has access to the full catalog.');
      } else {
        toast.success('Subscription activated! Your 7-day trial has started.');
      }
      setSearchParams({});
      await loadData();
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify payment.');
      setSearchParams({});
      await loadData();
    }
  };

  const handleSelectPlan = async (planId) => {
    try {
      setActionLoading(planId);
      const res = await api.post('/subscriptions/create-checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setActionLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading('portal');
      const res = await api.post('/subscriptions/create-portal-session', {
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      toast.error('Failed to open billing portal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    try {
      setCanceling(true);
      await api.post('/subscriptions/cancel');
      toast.success('Subscription will be canceled at the end of your billing period.');
      setCancelOpen(false);
      loadData();
    } catch {
      toast.error('Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading('reactivate');
      await api.post('/subscriptions/reactivate');
      toast.success('Subscription reactivated!');
      loadData();
    } catch {
      toast.error('Failed to reactivate');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="subscription-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const currentPlan = subscription?.plan;
  const currentType = subscription?.subscription_type || 'self_managed';
  const status = subscription?.status;
  const hasSubscription = subscription?.has_subscription;

  // Normalize legacy plan IDs to new format
  const normalizedPlan = currentPlan === 'monthly' ? 'self_managed_monthly'
    : currentPlan === 'annual' ? 'self_managed_annual'
    : currentPlan;

  const selfManagedPlans = plans.filter(p => p.subscription_type === 'self_managed');
  const fulfillmentPlans = plans.filter(p => p.subscription_type === 'fulfillment');

  // Self-Managed plans are hidden from new selection for now. Only keep
  // showing the Self-Managed card if the tenant *actually* has an active
  // Self-Managed subscription (so they can see/cancel/manage it). The
  // status endpoint defaults `subscription_type` to "self_managed" even
  // when no real subscription exists — `has_subscription` is the truth.
  const showSelfManaged = currentType === 'self_managed' && hasSubscription;

  const visibleSelfManaged = showSelfManaged
    ? selfManagedPlans.find(p => p.interval === billingInterval)
    : null;
  const visibleFulfillment = fulfillmentPlans.find(p => p.interval === billingInterval);

  return (
    <div className="space-y-6" data-testid="subscription-settings">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Subscription & Billing
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage your plan, billing cycle, and payment methods</p>
        </div>
        {hasSubscription && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleManageBilling}
            disabled={actionLoading === 'portal'}
            className="flex items-center gap-2"
            data-testid="manage-billing-btn"
          >
            <CreditCard className="h-4 w-4" />
            Payment Methods
            <ExternalLink className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Status Banner */}
      <StatusBanner subscription={subscription} onReactivate={handleReactivate} actionLoading={actionLoading} />

      {/* Billing Interval Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="billing-toggle">
          <button
            onClick={() => setBillingInterval('month')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              billingInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="toggle-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('year')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              billingInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="toggle-annual"
          >
            Annual
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className={`grid grid-cols-1 ${visibleSelfManaged ? 'md:grid-cols-2' : 'max-w-md mx-auto'} gap-6`}>
        {visibleSelfManaged && (
          <PlanCard
            plan={visibleSelfManaged}
            isCurrentPlan={normalizedPlan === visibleSelfManaged.id}
            canSelect={normalizedPlan !== visibleSelfManaged.id}
            onSelect={() => handleSelectPlan(visibleSelfManaged.id)}
            loading={actionLoading === visibleSelfManaged.id}
            icon={<Package className="h-7 w-7 text-[#4A6741]" />}
            accentColor="#4A6741"
            status={status}
            hasSubscription={hasSubscription}
          />
        )}
        {visibleFulfillment && (
          <PlanCard
            plan={visibleFulfillment}
            isCurrentPlan={normalizedPlan === visibleFulfillment.id}
            canSelect={normalizedPlan !== visibleFulfillment.id}
            onSelect={() => handleSelectPlan(visibleFulfillment.id)}
            loading={actionLoading === visibleFulfillment.id}
            icon={<Wallet className="h-7 w-7 text-blue-600" />}
            accentColor="#2563EB"
            popular={!!visibleSelfManaged}
            status={status}
            hasSubscription={hasSubscription}
          />
        )}
      </div>

      {/* Cancel Subscription */}
      {(hasSubscription || ['trialing', 'active'].includes(status)) && status && !['canceled', 'expired', 'canceling'].includes(status) && (
        <div className="text-center pt-2">
          <button
            onClick={() => setCancelOpen(true)}
            className="text-sm text-gray-400 hover:text-red-500 transition-colors underline"
            data-testid="cancel-subscription-link"
          >
            Cancel subscription
          </button>
        </div>
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Your store will remain active until the end of your current billing period. After that, your storefront will be disabled and customers won't be able to access your store.
              {currentType === 'fulfillment' && (
                <span className="block mt-2 font-medium text-red-600">
                  Golfshop.fit catalog will no longer be available on your storefront.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="keep-plan-btn">
              Keep My Plan
            </Button>
            <Button
              onClick={handleCancel}
              disabled={canceling}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-cancel-btn"
            >
              {canceling ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Canceling...</> : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-medium text-gray-900">What happens when my trial ends?</h3>
            <p className="text-gray-500">Your card will be automatically charged. You'll get an email reminder before the trial ends.</p>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Can I switch plans?</h3>
            <p className="text-gray-500">Yes — select a different plan above and you'll be taken to checkout. Changes are prorated based on your billing cycle.</p>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">What happens if I cancel?</h3>
            <p className="text-gray-500">You keep access until the end of your billing period. After that, your storefront goes offline but your data stays safe. Resubscribe anytime.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ subscription, onReactivate, actionLoading }) {
  if (!subscription) return null;
  const { status, days_remaining_in_trial, subscription_type, plan } = subscription;

  if (status === 'trialing') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
        <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
        <div>
          <p className="font-medium text-blue-800">Trial Active — {days_remaining_in_trial} days remaining</p>
          <p className="text-sm text-blue-600">Your store is live. Payment begins after trial.</p>
        </div>
      </div>
    );
  }

  if (status === 'active') {
    const planLabel = plan?.includes('fulfillment') ? 'Fulfillment' : 'Self-Managed';
    const interval = plan?.includes('annual') ? 'year' : 'month';
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
        <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
        <p className="font-medium text-green-800">Active — {planLabel} ({interval}ly billing)</p>
      </div>
    );
  }

  if (status === 'canceling') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">Canceling</p>
            <p className="text-sm text-yellow-600">Your store stays active until the end of the billing period.</p>
          </div>
        </div>
        <Button size="sm" onClick={onReactivate} disabled={actionLoading === 'reactivate'} className="bg-green-600 hover:bg-green-700 text-white" data-testid="reactivate-btn">
          {actionLoading === 'reactivate' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reactivate'}
        </Button>
      </div>
    );
  }

  if (status === 'past_due') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <div>
          <p className="font-medium text-red-800">Payment Failed</p>
          <p className="text-sm text-red-600">Please update your payment method to keep your store active.</p>
        </div>
      </div>
    );
  }

  if (['canceled', 'expired'].includes(status)) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
        <XCircle className="h-5 w-5 text-gray-500 flex-shrink-0" />
        <div>
          <p className="font-medium text-gray-800">Subscription Inactive</p>
          <p className="text-sm text-gray-500">Select a plan below to reactivate your store.</p>
        </div>
      </div>
    );
  }

  // Default / pending
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
      <Clock className="h-5 w-5 text-gray-500 flex-shrink-0" />
      <div>
        <p className="font-medium text-gray-800">Choose a Plan</p>
        <p className="text-sm text-gray-500">Select a subscription to activate your store. All plans include a 7-day free trial.</p>
      </div>
    </div>
  );
}

function PlanCard({ plan, isCurrentPlan, canSelect, onSelect, loading, icon, accentColor, popular, status, hasSubscription }) {
  const isFulfillment = plan.subscription_type === 'fulfillment';

  return (
    <div
      className={`relative rounded-xl border-2 p-6 transition-all ${
        isCurrentPlan
          ? `border-[${accentColor}] bg-[${accentColor}]/5 shadow-md`
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      }`}
      style={isCurrentPlan ? { borderColor: accentColor, backgroundColor: `${accentColor}0D` } : {}}
      data-testid={`plan-${plan.id}`}
    >
      {popular && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-bold rounded-full text-white" style={{ backgroundColor: accentColor }}>
          Most Popular
        </div>
      )}
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4 px-3 py-0.5 text-xs font-bold rounded-full text-white" style={{ backgroundColor: accentColor }}>
          Current Plan
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        {icon}
        <div>
          <h3 className="font-bold text-gray-900">{isFulfillment ? 'Fulfillment' : 'Self-Managed'}</h3>
          {isFulfillment && <p className="text-xs text-gray-500">by Golfshop.fit</p>}
        </div>
      </div>

      <div className="mb-4">
        <span className="text-3xl font-black text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          ${plan.price.toLocaleString()}
        </span>
        <span className="text-sm text-gray-500">/{plan.interval}</span>
        {plan.badge && (
          <span className="ml-2 inline-block text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: accentColor }}>
            {plan.badge}
          </span>
        )}
      </div>

      <ul className="space-y-2 text-sm mb-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-gray-600">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
            {f}
          </li>
        ))}
      </ul>

      {isCurrentPlan ? (
        <div className="w-full text-center py-2.5 rounded-lg text-sm font-medium text-gray-500 bg-gray-100">
          Your current plan
        </div>
      ) : canSelect ? (
        <Button
          className="w-full py-5 font-bold text-white"
          style={{ backgroundColor: accentColor }}
          onClick={onSelect}
          disabled={loading}
          data-testid={`select-${plan.id}`}
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting to checkout...</>
          ) : (
            <><CreditCard className="h-4 w-4 mr-2" />{hasSubscription ? 'Switch Plan' : `Start Free Trial`}</>
          )}
        </Button>
      ) : null}
    </div>
  );
}
